/**
 * Shared Embla Carousel initialization for custom sections.
 * Data attributes on [data-embla] elements:
 *   data-embla-config     — JSON: { slidesPerView, spaceBetween, breakpoints: { "769": { slidesPerView: 2 } } }
 *   data-embla-pagination — CSS selector for dot-pagination container
 */
(function () {
  'use strict';

  function mqKey(px) {
    return '(min-width: ' + px + 'px)';
  }

  function convertConfig(raw) {
    var slidesPerView = raw.slidesPerView || 1;
    var spaceBetween = raw.spaceBetween || 0;
    var breakpoints = raw.breakpoints || {};
    var emblaBreakpoints = {};

    Object.keys(breakpoints).forEach(function (bp) {
      var val = breakpoints[bp];
      emblaBreakpoints[mqKey(bp)] = {
        slidesToScroll: val.slidesPerView || slidesPerView,
        _slidesPerView: val.slidesPerView || slidesPerView,
        _spaceBetween: typeof val.spaceBetween === 'number' ? val.spaceBetween : spaceBetween,
      };
    });

    return {
      emblaOptions: {
        align: 'start',
        containScroll: 'trimSnaps',
        slidesToScroll: slidesPerView,
        breakpoints: emblaBreakpoints,
      },
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetween,
      rawBreakpoints: breakpoints,
    };
  }

  function applySlideSizing(containerEl, slidesPerView, spaceBetween) {
    var slides = containerEl.children;
    containerEl.style.gap = spaceBetween + 'px';

    for (var i = 0; i < slides.length; i++) {
      if (slidesPerView <= 1) {
        slides[i].style.flex = '0 0 100%';
      } else {
        var totalGap = spaceBetween * (slidesPerView - 1);
        slides[i].style.flex = '0 0 calc((100% - ' + totalGap + 'px) / ' + slidesPerView + ')';
      }
      slides[i].style.minWidth = '0';
    }
  }

  function getActiveValues(cfg) {
    var spv = cfg.slidesPerView;
    var sb = cfg.spaceBetween;
    var sortedBps = Object.keys(cfg.rawBreakpoints).map(Number).sort(function (a, b) { return a - b; });

    sortedBps.forEach(function (bp) {
      if (window.matchMedia(mqKey(bp)).matches) {
        var val = cfg.rawBreakpoints[bp];
        if (val.slidesPerView) spv = val.slidesPerView;
        if (typeof val.spaceBetween === 'number') sb = val.spaceBetween;
      }
    });

    return { slidesPerView: spv, spaceBetween: sb };
  }

  function setupPagination(emblaApi, dotsContainer) {
    if (!dotsContainer) return function () {};
    var dotNodes = [];

    function createDots() {
      dotsContainer.innerHTML = '';
      dotNodes = [];
      emblaApi.scrollSnapList().forEach(function (_, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'embla__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (index + 1));
        dot.addEventListener('click', function () { emblaApi.scrollTo(index); });
        dotNodes.push(dot);
        dotsContainer.appendChild(dot);
      });
    }

    function updateDots() {
      var selected = emblaApi.selectedScrollSnap();
      dotNodes.forEach(function (dot, i) {
        dot.classList.toggle('embla__dot--active', i === selected);
      });
    }

    createDots();
    updateDots();
    emblaApi.on('select', updateDots);
    emblaApi.on('reInit', function () { createDots(); updateDots(); });

    return function () { emblaApi.off('select', updateDots); };
  }

  function debounce(fn, delay) {
    var timer;
    return function () { clearTimeout(timer); timer = setTimeout(fn, delay); };
  }

  function initEmbla(root) {
    if (root._emblaApi) {
      root._emblaApi.destroy();
      root._emblaApi = null;
    }

    var rawConfig;
    try {
      rawConfig = JSON.parse(root.dataset.emblaConfig || '{}');
    } catch (e) {
      console.error('[embla-init] Invalid config on', root);
      return;
    }

    var cfg = convertConfig(rawConfig);
    var viewportEl = root.querySelector('.embla__viewport');
    var containerEl = root.querySelector('.embla__container');

    if (!viewportEl || !containerEl) {
      console.error('[embla-init] Missing .embla__viewport or .embla__container in', root);
      return;
    }

    var active = getActiveValues(cfg);
    applySlideSizing(containerEl, active.slidesPerView, active.spaceBetween);

    var emblaApi = EmblaCarousel(viewportEl, cfg.emblaOptions);
    root._emblaApi = emblaApi;

    var paginationSelector = root.dataset.emblaPagination;
    var dotsContainer = paginationSelector ? root.querySelector(paginationSelector) : null;
    setupPagination(emblaApi, dotsContainer);

    var resizeHandler = debounce(function () {
      var current = getActiveValues(cfg);
      applySlideSizing(containerEl, current.slidesPerView, current.spaceBetween);
      emblaApi.reInit();
    }, 150);
    window.addEventListener('resize', resizeHandler);

    root._emblaCleanup = function () {
      window.removeEventListener('resize', resizeHandler);
      if (root._emblaApi) {
        root._emblaApi.destroy();
        root._emblaApi = null;
      }
    };
  }

  function initAllEmbla() {
    document.querySelectorAll('[data-embla]').forEach(initEmbla);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllEmbla);
  } else {
    initAllEmbla();
  }

  document.addEventListener('shopify:section:load', initAllEmbla);
})();
